import React, { useMemo, useRef } from "react";
import { Calculator, Check, DatabaseZap, RefreshCw } from "lucide-react";
import { Modal } from "../ui/Modal";
import { Button, Skeleton } from "../ui/primitives";
import { inrFull } from "../../lib/utils";
import { annualEffectiveRatePaise, fromConfig } from "../../lib/pricingBands";
import { LazyMotion, domAnimation } from "framer-motion";
import PricingEstimator from "../../pages/landingparts/PricingEstimator";

export const PRICING_FEATURE_STATUS = Object.freeze({
  LOADING: "loading",
  READY: "ready",
  ERROR: "error",
});

/**
 * Project the public tier matrix into the one offering used by the per-student
 * marketing view. The API remains authoritative: no missing feature or plan is
 * synthesized in the browser.
 */
export function projectPerStudentPlanFeatures(planMatrix) {
  const coreModules = Array.isArray(planMatrix?.core_modules) ? planMatrix.core_modules : null;
  const catalogue = Array.isArray(planMatrix?.features) ? planMatrix.features : null;
  const plans = Array.isArray(planMatrix?.plans) ? planMatrix.plans : null;
  const coreModulesValid = coreModules
    && coreModules.every((moduleId) => typeof moduleId === "string" && moduleId.trim())
    && new Set(coreModules).size === coreModules.length;
  if (!coreModulesValid || !catalogue || !plans) {
    return { valid: false, coreModules: [], includedFeatures: [], plan: null };
  }

  const plan = plans.find((candidate) => candidate?.id === "plan_growth");
  if (!plan || !plan.features || typeof plan.features !== "object" || Array.isArray(plan.features)) {
    return { valid: false, coreModules: [], includedFeatures: [], plan: null };
  }

  const catalogueIds = catalogue.map((feature) => feature?.id);
  const catalogueValid = catalogue.every((feature) => (
    feature
    && typeof feature.id === "string"
    && feature.id.trim()
    && typeof feature.label === "string"
    && feature.label.trim()
    && typeof feature.readiness_gated === "boolean"
    && feature.default_state === (
      feature.readiness_gated
        ? "disabled_until_platform_enablement"
        : "plan_dependent"
    )
    && typeof plan.features[feature.id] === "boolean"
  )) && new Set(catalogueIds).size === catalogueIds.length;
  if (!catalogueValid) return { valid: false, coreModules: [], includedFeatures: [], plan: null };

  return {
    valid: true,
    coreModules: [...coreModules],
    plan,
    includedFeatures: catalogue
      .filter((feature) => !feature.readiness_gated && plan.features[feature.id] === true)
      .map((feature) => ({ id: feature.id, label: feature.label })),
  };
}

/**
 * Validate the exact per-student fields before `fromConfig` is allowed to
 * normalize them. `fromConfig` deliberately supports offline fallbacks for
 * other marketing surfaces; this evidence dialog must instead fail closed.
 */
export function hasVerifiedPerStudentPricingModel(pricingModel, provenance) {
  if (
    !provenance
    || provenance.verified !== true
    || provenance.source !== "platform_settings"
    || provenance.reason != null
  ) return false;
  if (!pricingModel || typeof pricingModel !== "object" || pricingModel.currency !== "INR") return false;
  if (!Array.isArray(pricingModel.bands) || pricingModel.bands.length === 0) return false;
  const ids = new Set();
  const bandsValid = pricingModel.bands.every((band) => {
    if (
      !band
      || typeof band.key !== "string"
      || !band.key.trim()
      || typeof band.label !== "string"
      || !band.label.trim()
      || !Number.isInteger(band.monthly_paise)
      || band.monthly_paise < 0
      || ids.has(band.key)
    ) return false;
    ids.add(band.key);
    return true;
  });
  return bandsValid
    && Number.isInteger(pricingModel.annual_discount_bps)
    && pricingModel.annual_discount_bps >= 0
    && pricingModel.annual_discount_bps < 10000
    && Number.isInteger(pricingModel.floor_monthly_paise)
    && pricingModel.floor_monthly_paise >= 0
    && Number.isInteger(pricingModel.ai_pack_monthly_paise)
    && pricingModel.ai_pack_monthly_paise >= 0;
}

const CORE_MODULE_LABELS = Object.freeze({
  hr: "HR",
  lessonplan: "Lesson plans",
  ptm: "PTM",
  reportcards: "Report cards",
});

function moduleLabel(moduleId) {
  if (CORE_MODULE_LABELS[moduleId]) return CORE_MODULE_LABELS[moduleId];
  return String(moduleId || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function FeatureEvidence({ coreModules, featureStatus, includedFeatures, onRetry, sourceLabel }) {
  if (featureStatus === PRICING_FEATURE_STATUS.LOADING) {
    return (
      <div className="space-y-3" role="status" aria-busy="true" aria-label="Loading included features" data-testid="per-student-features-loading">
        <Skeleton className="h-5 w-44" />
        <div className="grid gap-2 sm:grid-cols-2">
          {[0, 1, 2, 3].map((item) => <Skeleton className="h-11 w-full rounded-xl" key={item} />)}
        </div>
      </div>
    );
  }

  if (featureStatus === PRICING_FEATURE_STATUS.ERROR) {
    return (
      <div className="rounded-2xl border border-critical/30 bg-critical-soft p-4" role="alert" data-testid="per-student-features-error">
        <p className="font-semibold text-ink">Feature details are unavailable</p>
        <p className="mt-1 text-sm text-ink-2">We could not load the feature list just now. Rather than show an unchecked list, we have left it out — try again in a moment.</p>
        {onRetry ? (
          <Button className="mt-3" onClick={onRetry} size="sm" type="button" variant="outline">
            <RefreshCw className="h-4 w-4" aria-hidden="true" /> Retry feature details
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="per-student-feature-evidence">
      <section aria-labelledby="per-student-included-heading">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">{sourceLabel}</p>
            <h4 className="mt-1 font-heading text-lg font-semibold text-ink" id="per-student-included-heading">Projected plan inclusions</h4>
          </div>
          <span className="text-xs font-semibold tabular-nums text-ink-subtle">{includedFeatures.length} features</span>
        </div>
        {includedFeatures.length > 0 ? (
          <ul className="mt-3 grid gap-2 sm:grid-cols-2" data-testid="per-student-included-features">
            {includedFeatures.map((feature) => (
              <li className="flex min-w-0 items-start gap-2.5 rounded-xl border border-line bg-surface p-3 text-sm text-ink-2" data-feature-id={feature.id} key={feature.id}>
                <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary/10">
                  <Check className="h-3 w-3 text-primary" strokeWidth={3} aria-hidden="true" />
                </span>
                <span className="leading-snug">{feature.label}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 rounded-xl border border-line bg-muted p-3 text-sm text-ink-subtle" data-testid="per-student-features-empty">
            This plan has no add-on features beyond the core school modules listed below.
          </p>
        )}
      </section>

      {coreModules.length > 0 ? (
        <section aria-labelledby="per-student-core-heading">
          <div className="flex flex-wrap items-end justify-between gap-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">Always included</p>
              <h4 className="mt-1 font-heading text-lg font-semibold text-ink" id="per-student-core-heading">Core school operations</h4>
            </div>
            <span className="text-xs font-semibold tabular-nums text-ink-subtle">{coreModules.length} modules</span>
          </div>
          <ul className="mt-3 flex flex-wrap gap-2" data-testid="per-student-core-modules">
            {coreModules.map((moduleId) => (
              <li className="rounded-lg border border-line bg-muted px-2.5 py-1.5 text-xs font-medium text-ink-2" data-core-module-id={moduleId} key={moduleId}>
                {moduleLabel(moduleId)}
              </li>
            ))}
          </ul>
        </section>
      ) : (
        <div className="flex items-start gap-3 rounded-xl border border-line bg-muted p-3 text-sm text-ink-2" data-testid="per-student-core-summary">
          <DatabaseZap className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <p>We could not load the list of core modules included in every plan. Everything else on this page is still accurate.</p>
        </div>
      )}
    </div>
  );
}

export default function PerStudentPricingModal({
  coreModules = [],
  featureStatus = PRICING_FEATURE_STATUS.LOADING,
  includedFeatures = [],
  onClose,
  onPrimaryAction,
  onPricingRetry,
  onRetry,
  open,
  primaryActionLabel = "Request reviewed proposal",
  pricingFacts = [],
  pricingModel,
  pricingProvenance,
  pricingStatus = PRICING_FEATURE_STATUS.READY,
  sourceLabel = "Current Growth feature set",
  taxDisplay = "Tax treatment unavailable",
}) {
  const resolvedTaxDisplay = typeof taxDisplay === "string" && taxDisplay.trim()
    ? taxDisplay.trim()
    : "Tax treatment unavailable";
  const pricingVerified = useMemo(
    () => hasVerifiedPerStudentPricingModel(pricingModel, pricingProvenance),
    [pricingModel, pricingProvenance],
  );
  const resolvedPricingStatus = pricingStatus === PRICING_FEATURE_STATUS.LOADING
    ? PRICING_FEATURE_STATUS.LOADING
    : (pricingStatus === PRICING_FEATURE_STATUS.ERROR || !pricingVerified
      ? PRICING_FEATURE_STATUS.ERROR
      : PRICING_FEATURE_STATUS.READY);
  const model = useMemo(
    () => (pricingVerified ? fromConfig(pricingModel) : null),
    [pricingModel, pricingVerified],
  );
  const initialFocusRef = useRef(null);
  const discountLabel = model
    ? `${parseFloat((model.annualDiscountBps / 100).toFixed(2))}%`
    : null;
  const footer = (
    <>
      <Button onClick={onClose} type="button" variant="ghost">Close</Button>
      {onPrimaryAction ? <Button onClick={onPrimaryAction} type="button">{primaryActionLabel}</Button> : null}
    </>
  );

  return (
    <Modal
      description="A planning estimate and the projected Growth plan feature set for a reviewed per-student proposal. This is not the current self-serve billing path."
      footer={footer}
      initialFocusRef={initialFocusRef}
      layerClassName="z-[120]"
      onClose={onClose}
      open={open}
      size="xl"
      title="Per-student proposal"
    >
      <div className="space-y-6" data-testid="per-student-pricing-modal">
        <div ref={initialFocusRef} tabIndex={-1} className="outline-none">
          {resolvedPricingStatus === PRICING_FEATURE_STATUS.LOADING ? (
          <section className="space-y-3 rounded-2xl border border-line bg-surface p-4 sm:p-6" aria-busy="true" aria-label="Loading per-student prices" data-testid="per-student-pricing-loading">
            <Skeleton className="h-6 w-56" />
            <Skeleton className="h-4 w-full max-w-xl" />
            <div className="grid gap-2 sm:grid-cols-3">
              {[0, 1, 2].map((item) => <Skeleton className="h-24 w-full rounded-xl" key={item} />)}
            </div>
          </section>
        ) : resolvedPricingStatus === PRICING_FEATURE_STATUS.ERROR ? (
          <section className="rounded-2xl border border-critical/30 bg-critical-soft p-4 sm:p-6" role="alert" data-testid="per-student-pricing-error">
            <p className="font-semibold text-ink">Verified prices are unavailable</p>
            <p className="mt-1 text-sm text-ink-2">We could not confirm current prices just now. Rather than show a rate we cannot stand behind, we have left them out — try again in a moment.</p>
            {(onPricingRetry || onRetry) ? (
              <Button className="mt-3" onClick={onPricingRetry || onRetry} size="sm" type="button" variant="outline">
                <RefreshCw className="h-4 w-4" aria-hidden="true" /> Retry pricing details
              </Button>
            ) : null}
          </section>
        ) : (
          <section className="overflow-hidden rounded-2xl border border-primary/20 bg-surface p-4 shadow-[0_18px_48px_rgba(37,99,235,0.08)] sm:p-6" aria-labelledby="per-student-summary-heading">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary text-white shadow-sm">
                <Calculator className="h-5 w-5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">Planning model · reviewed proposal required</p>
                <h3 className="mt-1 font-heading text-xl font-bold tracking-tight text-ink sm:text-2xl" id="per-student-summary-heading">Estimate by the students you serve</h3>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ink-2">Rates come from the verified proposal configuration. Its annual-estimate policy applies a {discountLabel} reduction. Tax display: {resolvedTaxDisplay}. A written schedule is required before purchase.</p>
              </div>
            </div>
            <dl className="mt-5 grid gap-2 sm:grid-cols-3" data-testid="per-student-rate-summary">
              {model.bands.map((band) => (
                <div className="rounded-xl border border-line bg-surface/90 p-3" key={band.id}>
                  <dt className="text-xs font-medium text-ink-subtle">{band.label}</dt>
                  <dd className="mt-1 font-heading text-lg font-bold tabular-nums text-ink">{inrFull(band.ratePaise)}<span className="ml-1 font-sans text-xs font-medium text-ink-subtle">/student/mo estimate</span></dd>
                  <dd className="mt-1 text-xs tabular-nums text-primary">{inrFull(annualEffectiveRatePaise(band.ratePaise, model.annualDiscountBps))}/student/mo effective in the annual proposal</dd>
                </div>
              ))}
            </dl>
            {pricingFacts.length > 0 ? (
              <dl className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4" data-testid="per-student-pricing-facts">
                {pricingFacts.map((fact) => (
                  <div className="min-w-0 rounded-xl border border-line bg-muted/70 p-3" key={fact.label}>
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">{fact.label}</dt>
                    <dd className="mt-1 break-words text-sm font-medium tabular-nums text-ink">{fact.value}</dd>
                  </div>
                ))}
              </dl>
            ) : null}
          </section>
          )}
        </div>

        <section className="rounded-2xl border border-line bg-muted/35 p-4 sm:p-6" aria-labelledby="per-student-features-heading">
          <div className="mb-5">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">Projected plan evidence</p>
            <h3 className="mt-1 font-heading text-xl font-bold text-ink" id="per-student-features-heading">What the proposal is modelled on</h3>
            <p className="mt-2 text-sm leading-relaxed text-ink-2">This is what the Growth plan includes today. Availability for your school and the final price are confirmed in a written proposal.</p>
          </div>
          <FeatureEvidence
            coreModules={coreModules}
            featureStatus={featureStatus}
            includedFeatures={includedFeatures}
            onRetry={onRetry}
            sourceLabel={sourceLabel}
          />
        </section>

        {/* PERF-6: PricingEstimator reuses the marketing `Reveal` primitive, which renders framer-motion
            `m` components — those need a LazyMotion feature bundle above them. On the marketing pricing
            page MarketingMotion supplies it; this modal is the ONE place the estimator mounts inside the
            platform console, where no marketing provider exists, so it carries its own boundary (without
            it the reveal would stay at its `initial` state and the estimator would render invisible). */}
        {resolvedPricingStatus === PRICING_FEATURE_STATUS.READY ? (
          <LazyMotion features={domAnimation} strict>
            <PricingEstimator embedded perStudentOnly pricingModel={pricingModel} taxDisplay={resolvedTaxDisplay} />
          </LazyMotion>
        ) : null}
      </div>
    </Modal>
  );
}
